import React, { useMemo, useState } from 'react';
import {
  FiArrowLeft,
  FiBookmark,
  FiCheckCircle,
  FiExternalLink,
  FiMessageCircle,
  FiSend,
  FiShare2,
  FiThumbsUp,
} from 'react-icons/fi';
import MediaCover from './MediaCover';
import { formatPublished, formatViews } from './MediaCard';

function getYoutubeEmbedUrl(value = '') {
  if (!value) return '';
  try {
    const url = new URL(value);
    if (url.hostname.includes('youtu.be')) return `https://www.youtube.com/embed/${url.pathname.slice(1)}`;
    if (url.hostname.includes('youtube.com')) {
      const videoId = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }
  } catch (error) {
    return '';
  }
  return '';
}

const MediaDetail = ({ item, relatedItems, onBack, onOpenItem, onOpenTarget }) => {
  const [bookmarked, setBookmarked] = useState(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([
    { id: 1, author: '학습자', text: '교육 내용과 대상이 명확해서 이해하기 쉬웠어요.' },
  ]);
  const [liked, setLiked] = useState(false);
  const [shareLabel, setShareLabel] = useState('공유');
  const embedUrl = useMemo(() => getYoutubeEmbedUrl(item.mediaUrl), [item.mediaUrl]);
  const isDirectVideo = /\.(mp4|webm|ogg)(\?.*)?$/i.test(item.mediaUrl || '');

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, text: item.description, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareLabel('복사됨');
        window.setTimeout(() => setShareLabel('공유'), 1600);
      }
    } catch (error) {
      // 사용자가 공유 창을 닫은 경우에는 별도 알림을 표시하지 않습니다.
    }
  };

  const addComment = (event) => {
    event.preventDefault();
    const text = comment.trim();
    if (!text) return;
    setComments((current) => [...current, { id: Date.now(), author: '나', text }]);
    setComment('');
  };

  return (
    <div className="ai-media-detail">
      <button type="button" className="ai-media-detail__back" onClick={onBack}>
        <FiArrowLeft aria-hidden="true" /> 목록으로
      </button>

      <div className="ai-media-detail__layout">
        <main>
          <div className="ai-media-player">
            {embedUrl && <iframe src={embedUrl} title={item.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />}
            {!embedUrl && isDirectVideo && <video src={item.mediaUrl} poster={item.thumbnailUrl} controls />}
            {!embedUrl && !isDirectVideo && <MediaCover item={item} />}
          </div>

          <h1>{item.title}</h1>
          <div className="ai-media-detail__meta-actions">
            <p>조회 {formatViews(item.views)}회 · {formatPublished(item.publishedAt)}</p>
            <div>
              <button type="button" className={liked ? 'is-active' : ''} onClick={() => setLiked((value) => !value)}>
                <FiThumbsUp aria-hidden="true" /> {liked ? '좋아요 취소' : '좋아요'}
              </button>
              <button type="button" onClick={handleShare}><FiShare2 aria-hidden="true" /> {shareLabel}</button>
              <button type="button" className={bookmarked ? 'is-active' : ''} onClick={() => setBookmarked((value) => !value)}>
                <FiBookmark aria-hidden="true" /> {bookmarked ? '저장됨' : '저장'}
              </button>
            </div>
          </div>

          <section className="ai-media-detail__channel">
            <span className="ai-media-avatar">{item.organization?.charAt(0) || '온'}</span>
            <div>
              <strong>{item.organization} {item.verified && <FiCheckCircle aria-label="인증 기관" />}</strong>
              <small>{item.category}</small>
            </div>
            <button type="button">채널 소식 받기</button>
          </section>

          <section className="ai-media-detail__description">
            <strong>{item.publishedAt} 등록</strong>
            <p>{item.description}</p>
            {!!item.tags?.length && <div>{item.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>}
            {item.targetUrl && (
              <button type="button" onClick={() => onOpenTarget(item.targetUrl)}>
                자세히 알아보기 <FiExternalLink aria-hidden="true" />
              </button>
            )}
          </section>

          <section className="ai-media-comments">
            <h2><FiMessageCircle aria-hidden="true" /> 댓글 {comments.length}</h2>
            <form onSubmit={addComment}>
              <span className="ai-media-avatar">나</span>
              <input value={comment} onChange={(event) => setComment(event.target.value)} placeholder="댓글을 입력하세요." />
              <button type="submit" aria-label="댓글 등록"><FiSend aria-hidden="true" /></button>
            </form>
            <div>
              {comments.map((entry) => (
                <article key={entry.id}>
                  <span className="ai-media-avatar">{entry.author.charAt(0)}</span>
                  <p><strong>{entry.author}</strong><span>{entry.text}</span></p>
                </article>
              ))}
            </div>
          </section>
        </main>

        <aside className="ai-media-related">
          <h2>이어 볼 콘텐츠</h2>
          {relatedItems.map((related) => (
            <button type="button" onClick={() => onOpenItem(related)} key={related.id}>
              <MediaCover item={related} compact />
              <span>
                <strong>{related.title}</strong>
                <small>{related.organization}</small>
                <em>조회 {formatViews(related.views)}회</em>
              </span>
            </button>
          ))}
        </aside>
      </div>
    </div>
  );
};

export default MediaDetail;
